import React, { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';

import { adminApi, errorMessage } from './adminApi';
import { ReasonDialog } from './dialogs';
import { ActionsMenu, DataTable, DescList, EmptyState, PageHeader, Panel } from './kit';
import { documentLabel, formatDay, formatDocument, formatKmBR, formatMoneyBR, formatPhoneBR } from './ui';
import { formatPlate } from '../garage/format';

const ITEM_COLUMNS = [
  { key: 'description', label: 'Item', render: (i) => i.description || '—' },
  { key: 'quantity', label: 'Qtd.', width: '80px', align: 'right', render: (i) => i.quantity },
  { key: 'unit', label: 'Unitário', width: '120px', align: 'right', render: (i) => formatMoneyBR(i.unitValue) },
  { key: 'total', label: 'Total', width: '120px', align: 'right', render: (i) => formatMoneyBR(i.total) },
];

// Atendimento: dados, cliente (mascarado ate "ver dados completos") e itens.
export default function SalePage() {
  const { plate, key } = useParams();
  const [sale, setSale] = useState(null);
  const [items, setItems] = useState(null);
  const [error, setError] = useState(null);
  const [customer, setCustomer] = useState(null);
  const [dialog, setDialog] = useState(null);

  useEffect(() => {
    let active = true;
    adminApi.searchMaintenances(plate)
      .then((data) => {
        if (!active) return;
        const found = data.results.find((item) => item.key === key);
        if (!found) {
          setError('Atendimento não encontrado.');
          return;
        }
        setSale(found);
        const params = found.source === 'os'
          ? { source: 'os', companyId: found.companyId, saleId: found.saleId }
          : { source: 'qc', code: found.code, cnpj: found.companyCnpj };
        adminApi.maintenanceItems(params)
          .then((detail) => { if (active) setItems(detail); })
          .catch(() => { if (active) setItems({ items: [], failed: true }); });
      })
      .catch((err) => { if (active) setError(errorMessage(err, 'Não foi possível carregar o atendimento.')); });
    return () => { active = false; };
  }, [plate, key]);

  const crumbs = [
    { label: 'Manutenções', to: '/admin/maintenances' },
    { label: formatPlate(plate), to: `/admin/maintenances/${plate}` },
  ];

  if (error) {
    return (
      <div className='ax-page'>
        <PageHeader crumbs={crumbs} title='Atendimento' />
        <EmptyState title={error} action={<Link className='ax-btn' to={`/admin/maintenances/${plate}`}>Voltar ao histórico</Link>} />
      </div>
    );
  }
  if (!sale) {
    return (
      <div className='ax-page'>
        <PageHeader crumbs={crumbs} title='Carregando…' />
        <span className='ax-skeleton-block' aria-busy='true' />
      </div>
    );
  }

  const shown = customer || sale.customer;

  return (
    <div className='ax-page'>
      <PageHeader
        crumbs={crumbs}
        title={`${sale.type || 'Atendimento'} nº ${sale.code}`}
        meta={`${sale.companyName || 'Oficina não informada'} · ${formatDay(sale.date)} · ${sale.origin}`}
        actions={<ActionsMenu items={[sale.customer && sale.customer.canReveal && !customer && { label: 'Ver dados completos do cliente', hint: 'Pede motivo', onSelect: () => setDialog('reveal') }]} />}
      />

      <div className='ax-grid'>
        <Panel title='Atendimento'>
          <DescList items={[
            { label: 'Data', value: formatDay(sale.date) },
            { label: 'Tipo', value: sale.type },
            { label: 'Número', value: sale.code },
            { label: 'Placa', value: formatPlate(plate) },
            { label: 'Quilometragem', value: formatKmBR(sale.km) },
            { label: 'Valor', value: formatMoneyBR(sale.value) },
            { label: 'Oficina', value: sale.companyName },
            { label: 'CNPJ da oficina', value: sale.companyCnpj ? formatDocument(sale.companyCnpj) : null },
          ]} />
        </Panel>
        <Panel title='Cliente' action={customer ? <span className='ax-badge'>Dados completos · registrado</span> : null}>
          {shown ? (
            <DescList items={[
              { label: 'Nome', value: shown.name },
              { label: documentLabel(shown.document), value: shown.document ? formatDocument(shown.document) : null },
              { label: 'Telefone', value: shown.phone ? formatPhoneBR(shown.phone) : null },
              { label: 'E-mail', value: shown.email },
            ]} />
          ) : <p className='ax-muted'>A oficina não registrou cliente neste atendimento.</p>}
        </Panel>
      </div>

      <Panel title='Itens'>
        {!items && <span className='ax-skeleton-line' aria-busy='true' />}
        {items && items.failed && <p className='ax-alert is-warn'>Não foi possível carregar os itens deste atendimento.</p>}
        {items && !items.failed && (
          <DataTable
            columns={ITEM_COLUMNS}
            rows={items.items}
            rowKey={(item) => `${item.description}-${item.total}-${item.quantity}`}
            empty={<p className='ax-muted'>Sem itens registrados.</p>}
          />
        )}
        {items && items.total !== null && items.total !== undefined && (
          <p className='ax-total'>Total <strong>{formatMoneyBR(items.total)}</strong></p>
        )}
      </Panel>

      {dialog === 'reveal' && (
        <ReasonDialog
          title='Ver dados completos do cliente?'
          text='Documento, telefone e e-mail do cliente deste atendimento aparecem sem máscara.'
          confirmLabel='Ver dados'
          onConfirm={async (reason) => setCustomer(await adminApi.maintenanceCustomer(plate, key, reason))}
          onClose={() => setDialog(null)}
        />
      )}
    </div>
  );
}
